/**
 * La frise d'un chantier : du devis à l'encaissement, sur un seul écran.
 *
 * Pour suivre un client, il fallait ouvrir Devis, Chantiers, Suivi, Factures
 * et Règlements. La frise lit ce qui existe déjà — devis, factures,
 * interventions à l'agenda, statut du chantier — et en tire sept étapes,
 * l'étape en cours, et le geste qui la fait avancer. Rien n'est saisi deux
 * fois : chaque étape se remplit seule.
 *
 * Une règle d'honnêteté : beaucoup de chantiers ont leurs devis et factures
 * dans Extrabat. Quand le CRM n'en porte aucun, les étapes commerciales ne
 * sont pas « à faire » — elles sont INCONNUES, et la frise le dit.
 */

export type EtapeId =
  | 'devis_envoye'
  | 'signe'
  | 'acompte'
  | 'planifie'
  | 'travaux'
  | 'facture'
  | 'paye';

/**
 * - `fait` : l'étape est franchie ;
 * - `en_cours` : c'est là qu'on en est ;
 * - `a_venir` : plus tard ;
 * - `sans_objet` : ce chantier n'en a pas besoin (pas d'acompte demandé) ;
 * - `inconnu` : le CRM n'a pas de quoi le dire (devis et factures hors CRM).
 */
export type EtatEtape = 'fait' | 'en_cours' | 'a_venir' | 'sans_objet' | 'inconnu';

export interface ActionEtape {
  libelle: string;
  lien: string;
}

export interface EtapeFrise {
  id: EtapeId;
  libelle: string;
  etat: EtatEtape;
  /** « DEV-2026-0012 », « 3 interventions », « reste 1 716,00 € »… */
  detail: string | null;
  /** Le geste qui fait avancer cette étape, quand il y en a un. */
  action: ActionEtape | null;
}

export interface Frise {
  etapes: EtapeFrise[];
  /** L'étape en cours, ou `null` quand tout est fait. */
  enCours: EtapeId | null;
  /** Ni devis ni facture dans le CRM : les étapes commerciales sont inconnues. */
  horsCrm: boolean;
}

export interface DevisFrise {
  id: string;
  numero: string | null;
  statut: string;
}

export interface FactureFrise {
  id: string;
  numero: string | null;
  /** acompte | situation | finale | avoir | standard */
  kind: string;
  statut: string;
  montantTtc: number;
  montantPaye: number;
}

export interface InterventionFrise {
  /** ISO 8601. */
  debutAt: string;
  finAt: string | null;
  statut: string;
}

const DEVIS_ENVOYES = new Set([
  'envoye',
  'consulte',
  'signe',
  'converti_facture',
  'refuse',
  'expire',
]);
const DEVIS_SIGNES = new Set(['signe', 'converti_facture']);
const DEVIS_A_ENVOYER = new Set(['brouillon', 'a_envoyer']);
/** Une facture émise et toujours valable : ni brouillon, ni annulée par un avoir. */
const FACTURE_HORS_JEU = new Set(['brouillon', 'annulee_avoir']);
const CHANTIER_PLANIFIE_OU_PLUS = new Set([
  'planifie',
  'en_cours',
  'pause',
  'termine',
  'receptionne',
  'sav',
  'archive',
]);
const CHANTIER_TRAVAUX_FINIS = new Set(['termine', 'receptionne', 'sav', 'archive']);

const EUR = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
const JOUR = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  day: '2-digit',
  month: '2-digit',
});

function nom(d: { numero: string | null }, defaut: string): string {
  return d.numero !== null && d.numero !== '' && !d.numero.startsWith('BROUILLON')
    ? d.numero
    : defaut;
}

function reste(f: FactureFrise): number {
  return Math.round((f.montantTtc - f.montantPaye) * 100) / 100;
}

function estPayee(f: FactureFrise): boolean {
  return f.statut === 'payee' || reste(f) <= 0.005;
}

function jour(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : JOUR.format(d);
}

export function friseChantier(entree: {
  chantierStatut: string;
  devis: readonly DevisFrise[];
  factures: readonly FactureFrise[];
  interventions: readonly InterventionFrise[];
  /** ISO 8601 : l'instant de lecture, pour dire si les travaux ont commencé. */
  maintenant: string;
}): Frise {
  const devis = entree.devis.filter((d) => d.statut !== 'annule');
  const factures = entree.factures;
  const horsCrm = devis.length === 0 && factures.length === 0;
  const maintenant = Date.parse(entree.maintenant);

  // ----- Devis envoyé, signé ---------------------------------------------
  const signe = devis.find((d) => DEVIS_SIGNES.has(d.statut)) ?? null;
  const envoye = devis.find((d) => DEVIS_ENVOYES.has(d.statut)) ?? null;
  const aEnvoyer = devis.find((d) => DEVIS_A_ENVOYER.has(d.statut)) ?? null;
  // Facturé sans devis dans le CRM : les deux étapes du devis n'ont pas lieu d'être.
  const sansDevis = devis.length === 0 && factures.length > 0;

  const etapeDevis: EtapeFrise = horsCrm
    ? { id: 'devis_envoye', libelle: 'Devis envoyé', etat: 'inconnu', detail: null, action: null }
    : sansDevis
      ? {
          id: 'devis_envoye',
          libelle: 'Devis envoyé',
          etat: 'sans_objet',
          detail: 'pas de devis dans le CRM',
          action: null,
        }
      : envoye !== null || signe !== null
        ? {
            id: 'devis_envoye',
            libelle: 'Devis envoyé',
            etat: 'fait',
            detail: nom(signe ?? envoye ?? { numero: null }, 'devis'),
            action: null,
          }
        : {
            id: 'devis_envoye',
            libelle: 'Devis envoyé',
            etat: 'a_venir',
            detail: aEnvoyer === null ? null : nom(aEnvoyer, 'en préparation'),
            action:
              aEnvoyer === null
                ? null
                : { libelle: 'Envoyer le devis', lien: `/devis/${aEnvoyer.id}` },
          };

  const refuse =
    !signe &&
    devis.length > 0 &&
    devis.every((d) => d.statut === 'refuse' || d.statut === 'expire');
  const etapeSigne: EtapeFrise = horsCrm
    ? { id: 'signe', libelle: 'Signé', etat: 'inconnu', detail: null, action: null }
    : sansDevis
      ? { id: 'signe', libelle: 'Signé', etat: 'sans_objet', detail: null, action: null }
      : signe !== null
        ? { id: 'signe', libelle: 'Signé', etat: 'fait', detail: nom(signe, 'devis'), action: null }
        : {
            id: 'signe',
            libelle: 'Signé',
            etat: 'a_venir',
            detail: refuse ? 'devis refusé ou expiré' : null,
            action:
              envoye === null
                ? null
                : { libelle: 'Relancer le client', lien: `/devis/${envoye.id}` },
          };

  // ----- Acompte -----------------------------------------------------------
  const acomptes = factures.filter((f) => f.kind === 'acompte' && f.statut !== 'annulee_avoir');
  const acomptesEmis = acomptes.filter((f) => f.statut !== 'brouillon');
  const acompteBrouillon = acomptes.find((f) => f.statut === 'brouillon') ?? null;
  const acompteImpaye = acomptesEmis.find((f) => !estPayee(f)) ?? null;
  const etapeAcompte: EtapeFrise = horsCrm
    ? { id: 'acompte', libelle: 'Acompte reçu', etat: 'inconnu', detail: null, action: null }
    : acomptes.length === 0
      ? {
          id: 'acompte',
          libelle: 'Acompte reçu',
          etat: 'sans_objet',
          detail: 'pas d’acompte',
          action: null,
        }
      : acompteImpaye !== null
        ? {
            id: 'acompte',
            libelle: 'Acompte reçu',
            etat: 'a_venir',
            detail: `${nom(acompteImpaye, 'acompte')} · reste ${EUR.format(reste(acompteImpaye))}`,
            action: { libelle: 'Enregistrer le règlement', lien: `/factures/${acompteImpaye.id}` },
          }
        : acomptesEmis.length === 0 && acompteBrouillon !== null
          ? {
              id: 'acompte',
              libelle: 'Acompte reçu',
              etat: 'a_venir',
              detail: 'acompte en brouillon',
              action: { libelle: 'Émettre l’acompte', lien: `/factures/${acompteBrouillon.id}` },
            }
          : {
              id: 'acompte',
              libelle: 'Acompte reçu',
              etat: 'fait',
              detail: acomptesEmis.map((f) => nom(f, 'acompte')).join(', '),
              action: null,
            };

  // ----- Planifié, travaux -------------------------------------------------
  const interventions = entree.interventions.filter((e) => e.statut !== 'annule');
  const debuts = interventions.map((e) => Date.parse(e.debutAt)).filter((t) => !Number.isNaN(t));
  const fins = interventions
    .map((e) => Date.parse(e.finAt ?? e.debutAt))
    .filter((t) => !Number.isNaN(t));
  const premier = debuts.length === 0 ? null : Math.min(...debuts);
  const dernier = fins.length === 0 ? null : Math.max(...fins);
  const planifie = interventions.length > 0 || CHANTIER_PLANIFIE_OU_PLUS.has(entree.chantierStatut);
  const etapePlanifie: EtapeFrise = planifie
    ? {
        id: 'planifie',
        libelle: 'Planifié',
        etat: 'fait',
        detail:
          premier === null
            ? 'rien à l’agenda'
            : `${interventions.length > 1 ? `${String(interventions.length)} interventions, dès le ` : 'le '}${jour(new Date(premier).toISOString())}`,
        action: null,
      }
    : {
        id: 'planifie',
        libelle: 'Planifié',
        etat: 'a_venir',
        detail: null,
        action: { libelle: 'Placer à l’agenda', lien: '/agenda' },
      };

  const travauxFinis =
    CHANTIER_TRAVAUX_FINIS.has(entree.chantierStatut) || (dernier !== null && dernier < maintenant);
  const travauxCommences =
    travauxFinis ||
    entree.chantierStatut === 'en_cours' ||
    (premier !== null && premier <= maintenant);
  const etapeTravaux: EtapeFrise = {
    id: 'travaux',
    libelle: 'Travaux',
    etat: travauxFinis ? 'fait' : travauxCommences ? 'en_cours' : 'a_venir',
    detail: travauxFinis
      ? dernier === null
        ? 'terminés'
        : `terminés le ${jour(new Date(dernier).toISOString())}`
      : travauxCommences
        ? 'en cours'
        : null,
    action: null,
  };

  // ----- Facturé, payé -----------------------------------------------------
  const ventes = factures.filter((f) => f.kind !== 'avoir' && f.kind !== 'acompte');
  const emises = ventes.filter((f) => !FACTURE_HORS_JEU.has(f.statut));
  const soldees = emises.filter((f) => f.kind === 'standard' || f.kind === 'finale');
  const brouillon = ventes.find((f) => f.statut === 'brouillon') ?? null;
  const etapeFacture: EtapeFrise = horsCrm
    ? { id: 'facture', libelle: 'Facturé', etat: 'inconnu', detail: null, action: null }
    : soldees.length > 0
      ? {
          id: 'facture',
          libelle: 'Facturé',
          etat: 'fait',
          detail: soldees.map((f) => nom(f, 'facture')).join(', '),
          action: null,
        }
      : {
          id: 'facture',
          libelle: 'Facturé',
          etat: 'a_venir',
          detail:
            brouillon !== null
              ? 'facture en brouillon'
              : emises.length > 0
                ? `${String(emises.length)} situation${emises.length > 1 ? 's' : ''} émise${emises.length > 1 ? 's' : ''}`
                : null,
          action:
            brouillon !== null
              ? { libelle: 'Émettre la facture', lien: `/factures/${brouillon.id}` }
              : signe !== null
                ? { libelle: 'Convertir en facture', lien: `/devis/${signe.id}` }
                : null,
        };

  // Ce que le client doit encore : toutes les pièces émises, acomptes compris.
  const aEncaisser = factures.filter((f) => f.kind !== 'avoir' && !FACTURE_HORS_JEU.has(f.statut));
  const impayee = aEncaisser.find((f) => !estPayee(f)) ?? null;
  const resteDu = aEncaisser.reduce((t, f) => t + Math.max(0, reste(f)), 0);
  const etapePaye: EtapeFrise = horsCrm
    ? { id: 'paye', libelle: 'Payé', etat: 'inconnu', detail: null, action: null }
    : etapeFacture.etat === 'fait' && impayee === null
      ? { id: 'paye', libelle: 'Payé', etat: 'fait', detail: 'soldé', action: null }
      : {
          id: 'paye',
          libelle: 'Payé',
          etat: 'a_venir',
          detail: resteDu > 0.005 ? `reste ${EUR.format(Math.round(resteDu * 100) / 100)}` : null,
          action:
            impayee === null
              ? null
              : { libelle: 'Enregistrer un règlement', lien: `/factures/${impayee.id}` },
        };

  const etapes = [
    etapeDevis,
    etapeSigne,
    etapeAcompte,
    etapePlanifie,
    etapeTravaux,
    etapeFacture,
    etapePaye,
  ];

  // L'étape en cours : la première qui n'est ni franchie, ni sans objet, ni
  // inconnue. Les suivantes restent « à venir », même si l'une est déjà faite
  // (un chantier peut être planifié avant que l'acompte ne soit encaissé).
  const courante = etapes.find((e) => e.etat === 'en_cours' || e.etat === 'a_venir') ?? null;
  for (const e of etapes) {
    // Une seule étape en cours : des travaux commencés alors que l'acompte
    // n'est pas encaissé le disent dans leur détail, pas par une seconde
    // étape en surbrillance.
    if (e === courante) e.etat = 'en_cours';
    else if (e.etat === 'en_cours') e.etat = 'a_venir';
  }

  return { etapes, enCours: courante?.id ?? null, horsCrm };
}
